import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { company } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

function getNavItems(t) {
  return [
    { label: t('nav.home'), to: '/', id: 'home' },
    { label: t('nav.about'), to: '/about', id: 'about' },
    { label: t('nav.services'), to: '/services', id: 'services' },
    { label: t('nav.quote'), to: '/quote', id: 'quote' },
    { label: t('nav.track'), to: '/track', id: 'track' },
    { label: t('nav.office'), to: '/office', id: 'office' },
    { label: t('nav.contact'), to: '/contact', id: 'contact' }
  ];
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLang();
  const navItems = getNavItems(t);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Every navigation (header, footer, buttons, back/forward) opens the new page at the top.
  useEffect(() => { window.scrollTo(0, 0); }, [location.key]);

  // Also used directly on the links, so clicking the link of the page you are already on scrolls to the top too.
  const toTop = () => window.scrollTo(0, 0);

  return (
    <>
      <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 bg-white border-b border-[color:var(--line)] transition-shadow duration-300 ${scrolled ? 'shadow-lg shadow-[rgba(42,31,122,0.08)]' : ''}`}>
        <div className="w-full flex items-center justify-between gap-4 px-0 py-3">
          {/* Left: Logo */}
          <Link to="/" data-testid="header-logo" onClick={toTop} className="flex items-center gap-3 shrink-0">
            <img src={company.logo} alt={company.name} className="h-12 w-12 object-contain rounded" />
            <div className="leading-tight">
              <div className="font-bold text-lg tracking-wide text-[color:var(--brand)] notranslate" translate="no">VEDYAH EXIM</div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-[color:var(--muted)]">{t('company.tagline')}</div>
            </div>
          </Link>

          {/* Right: Nav buttons + Language */}
          <div className="flex items-center gap-4">
            <nav className="hidden xl:flex items-center gap-5" data-testid="header-nav">
              {navItems.map((n) => (
                <Link key={n.to} to={n.to} onClick={toTop} data-testid={`nav-${n.id}-link`} className={`nav-link ${location.pathname === n.to ? 'active' : ''}`}>
                  {n.label}
                </Link>
              ))}
            </nav>
            <LanguageSwitcher />
            <button
              data-testid="menu-open-btn"
              aria-label="Toggle menu"
              onClick={() => setOpen(true)}
              className="xl:hidden p-2 rounded hover:bg-[color:var(--brand-soft)] transition-colors"
            >
              <Menu size={26} className="text-[color:var(--ink)]" />
            </button>
          </div>
        </div>
      </header>

      {/* Fullscreen Menu Overlay */}
      <div className={`menu-overlay ${open ? 'open' : ''}`} data-testid="menu-overlay">
        <div className="h-full w-full flex flex-col">
          <div className="flex items-center justify-between px-6 lg:px-10 py-4">
            <div className="flex items-center gap-3 text-white">
              <img src={company.logo} alt={company.name} className="h-12 w-12 object-contain bg-white rounded" />
              <div>
                <div className="font-bold tracking-wide">VEDYAH EXIM</div>
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Global Partner In Movements</div>
              </div>
            </div>
            <button data-testid="menu-close-btn" aria-label="Close menu" onClick={() => setOpen(false)} className="text-white p-2 hover:rotate-90 transition-transform duration-300">
              <X size={28} />
            </button>
          </div>

          <nav className="flex-1 flex flex-col items-center justify-center gap-6 text-white">
            {navItems.map((n, i) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`menu-${n.id}-link`}
                onClick={() => { toTop(); setOpen(false); }}
                className={`menu-link text-3xl lg:text-4xl font-light tracking-wide ${location.pathname === n.to ? 'active' : 'opacity-80'}`}
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="px-6 lg:px-10 py-6 text-white/70 text-sm flex flex-wrap items-center justify-center gap-6">
            <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 hover:text-white transition-colors"><Phone size={16} /> {company.phone}</a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:text-white transition-colors"><Mail size={16} /> {company.email}</a>
          </div>
        </div>
      </div>
    </>
  );
}
